import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import {
  IResumeContactRequest,
  IResumeDatabaseAccess,
  getResumeDatabaseErrorMessage,
  parseResumeDatabaseError,
  useRequestResumeContactMutation,
} from '../../services/serviceResume';
import { ListingAllowanceSummary } from './accessPanels';

export const MESSAGE_MIN = 20;
export const MESSAGE_MAX = 1000;

// Mounted only while the dialog is open, so every open starts from clean
// mutation state (RTK Query 1.6 has no `reset()` on the mutation result).
export const IntroductionDialog = ({
  onClose,
  onCompleted,
  rguid,
  candidateName,
  access,
}: {
  onClose: () => void;
  onCompleted: (request: IResumeContactRequest) => void;
  rguid: string;
  candidateName: string;
  access?: IResumeDatabaseAccess;
}) => {
  const [message, setMessage] = useState('');
  const [classifiedId, setClassifiedId] = useState<number | ''>('');
  const [requestContact, requestResult] = useRequestResumeContactMutation();

  const listings = access?.listings || [];
  const selected = listings.filter((listing) => listing.classifiedId === classifiedId)[0];
  const trimmedLength = message.trim().length;
  const messageTooShort = trimmedLength > 0 && trimmedLength < MESSAGE_MIN;
  const allowanceExhausted = Boolean(selected && selected.introductionsRemaining <= 0);
  const parsedError = parseResumeDatabaseError(requestResult.error);
  const inCooldown = parsedError.code === 'resume_contact_cooldown';
  const errorMessage = requestResult.error
    ? getResumeDatabaseErrorMessage(requestResult.error)
      || 'We could not send that introduction request. Please try again.'
    : null;

  const canSubmit = Boolean(classifiedId)
    && !allowanceExhausted
    && trimmedLength >= MESSAGE_MIN
    && trimmedLength <= MESSAGE_MAX
    && !inCooldown
    && !requestResult.isLoading;

  const send = async () => {
    if (!canSubmit || !classifiedId) return;
    try {
      const request = await requestContact({
        rguid,
        classifiedId: classifiedId as number,
        message: message.trim(),
      }).unwrap();
      onCompleted(request);
    } catch {
      // The parsed policy error stays on screen so the employer can adjust.
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request an introduction</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {candidateName} sees your company, the job you select, and your message, then chooses
          whether to share their contact details and resume file. They can also decline, block you,
          or report the request.
        </Typography>

        {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}

        {!listings.length ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            An active Featured job listing is required to request an introduction.
          </Alert>
        ) : null}

        <TextField
          select
          value={classifiedId}
          onChange={(event) => setClassifiedId(event.target.value ? Number(event.target.value) : '')}
          label="Which job are you hiring for?"
          required
          fullWidth
          disabled={!listings.length}
          helperText="Introductions are always tied to one of your active Featured listings."
        >
          {listings.map((listing) => (
            <MenuItem key={listing.classifiedId} value={listing.classifiedId}>
              {listing.title || `Listing ${listing.classifiedId}`}
              {listing.introductionsRemaining <= 0 ? ' (no introductions left)' : ''}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          value={message}
          onChange={(event) => setMessage(event.target.value.slice(0, MESSAGE_MAX))}
          label="Why is this candidate a fit for that job?"
          required
          multiline
          minRows={4}
          fullWidth
          sx={{ mt: 2 }}
          error={messageTooShort}
          inputProps={{ maxLength: MESSAGE_MAX }}
          helperText={messageTooShort
            ? `Write at least ${MESSAGE_MIN} characters (${MESSAGE_MIN - trimmedLength} to go).`
            : `${MESSAGE_MAX - trimmedLength} characters remaining. Generic messages are the most common reason candidates decline.`}
        />

        <Box sx={{ mt: 2 }}>
          <ListingAllowanceSummary listings={listings} compact />
        </Box>

        {selected && !allowanceExhausted ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1.5 }}>
            Sending this uses one of the {selected.introductionsRemaining} introductions left on
            &ldquo;{selected.title || `Listing ${selected.classifiedId}`}&rdquo;, which your whole
            team shares.
          </Typography>
        ) : null}

        {allowanceExhausted ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            That listing has used all of its introductions for now. Pick another eligible listing or
            wait for the allowance to reset.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={send}>Send request</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntroductionDialog;
